import { Button } from '@fluentui/react-components'
import { useDotnetClient } from '@/contexts/DotnetClientContext'
import { toast } from 'react-toastify'
import { FluentCard } from '../FluentCard/FluentCard'
import './EntityFilesList.scss'
import { useEffect, useState } from 'react'
import { DocumentArrowUpRegular, Delete24Regular, ArrowDownload24Regular } from '@fluentui/react-icons'
import { ENDPOINTS } from '@/util'
import DOMPurify from 'isomorphic-dompurify'
import { getQueryString } from '@/dotnet-client/core/request'
import { MessagesWorkspacesEntity } from '@/dotnet-client/models/MessagesWorkspacesEntity'
import { MessagesStorageStorageDetailItem } from '@/dotnet-client/models/MessagesStorageStorageDetailItem'
import { MessagesStorageStorageFileType } from '@/dotnet-client/models/MessagesStorageStorageFileType'
import { ControlledDialog } from '../Dialog/ControlledDialog'
import { useSessionStorage } from '@/hooks/useSessionStorage'

type EntityFilesListProps = {
  onSubmit: () => void
  workspaceId: number
  selectedEntity: MessagesWorkspacesEntity
  fileType: MessagesStorageStorageFileType
}

export const EntityFilesList = (props: EntityFilesListProps) => {
  const { dmpClient, isDMPClientLoaded } = useDotnetClient()
  const [files, setFiles] = useState<MessagesStorageStorageDetailItem[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false)
  const [token] = useSessionStorage('token', '')

  const fetchTemplates = () => {
    dmpClient?.storageApi.getStorageApiGetFilesDetailsEntityTemplates(props.selectedEntity?.entityId).then((x) => {
      setFiles(x)
    })
  }

  const fetchDataAssets = () => {
    dmpClient?.storageApi.getStorageApiGetFilesDetailsEntityDataAssets(props.selectedEntity?.entityId).then((x) => {
      setFiles(x)
    })
  }

  const fetchFiles = () => {
    if (props.fileType == MessagesStorageStorageFileType.Template) fetchTemplates()
    else if (props.fileType == MessagesStorageStorageFileType.Result) fetchDataAssets()
  }

  useEffect(() => {
    if (isDMPClientLoaded) fetchFiles()
  }, [isDMPClientLoaded])

  const onDownloadClick = async (f: MessagesStorageStorageDetailItem) => {
    try {
      setLoading(true)

      const query = getQueryString({
        fileType: props.fileType,
        blobName: f.encryptedBlobName,
        subscriptionId: props.workspaceId,
      })
      const response = await fetch(`${ENDPOINTS.LANDING}/storage/download_file/workspace${query}`, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      })
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.setAttribute('href', url)
        a.setAttribute('download', f.filename ?? 'filename.txt')
        document.body.appendChild(a)
        a.click()
        a.remove()
      } else {
        let errMessage = 'Unknown error'
        if (response.statusText) errMessage = response.statusText
        try {
          const tryJson = await response.json()
          if (tryJson.Message) errMessage = tryJson.Message
        } catch (error) {
          // ignore
        }
        toast.error('Error downloading file: ' + DOMPurify.sanitize(errMessage))
      }
    } catch (error) {
      toast.error(`Error downloading file: ' + ${DOMPurify.sanitize(`${error}`)}`)
      console.error(DOMPurify.sanitize(`${error}`))
    } finally {
      setLoading(false)
    }
  }

  const onDeleteClick = async (f: MessagesStorageStorageDetailItem) => {
    setLoading(true)

    dmpClient?.storageApi
      .postStorageApiDeleteFileWorkspace(f.encryptedBlobName, props.workspaceId, props.fileType)
      .then((res) => {
        if (res) toast.success('File deleted.')
        else toast.warn('File was not deleted.')
      })
      .catch((error) => {
        toast.error(`Error downloading file: ' + ${DOMPurify.sanitize(`${error}`)}`)
        console.error(DOMPurify.sanitize(`${error}`))
      })
      .finally(() => {
        fetchFiles()
        setLoading(false)
      })
  }

  return (
    <div className="filesList">
      <div>{props.selectedEntity?.name} Files</div>
      {files.length === 0 && <div>No files found.</div>}
      {files.map((f) => (
        <FluentCard
          key={f.encryptedBlobName}
          title={f.filename}
          description={f.blobName}
          icon={<DocumentArrowUpRegular />}
          action={
            <>
              <Button
                appearance="transparent"
                icon={<ArrowDownload24Regular />}
                aria-label="Download File"
                onClick={() => onDownloadClick(f)}
                disabled={loading}
              />
              <Button
                appearance="transparent"
                icon={<Delete24Regular />}
                aria-label="Delete File"
                onClick={() => setIsDeleteDialogOpen(true)}
                disabled={loading}
              />

              <ControlledDialog
                open={isDeleteDialogOpen}
                setOpen={setIsDeleteDialogOpen}
                title={'Delete file'}
                content={`Please confirm you want to delete ${f.blobName} (${f.filename})?`}
                onConfirm={() => {
                  onDeleteClick(f)
                  setIsDeleteDialogOpen(false)
                }}
              />
            </>
          }
        ></FluentCard>
      ))}
    </div>
  )
}
